import type { CustomCellRendererProps } from 'ag-grid-react'
import { Store, GitFork, ImageIcon, Loader2, MessageCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ItemActionsMenu } from './ItemActionsMenu'
import { SkuSearchCell, type SkuSaveData } from './SkuSearchCell'
import { EditableCell } from './EditableCell'
import type { PlanDetailItem } from '@/types'
import type { DagRowRead } from '@/types/dag'
import type { RowData } from './useProductsTableState'
import { rowDataToPlanDetailItem } from './useProductsTableState'
import { useDagTableStore } from '@/stores/useDagTableStore'

// Option item type for expanded rows
export interface OptionItem {
  id: string
  name: string
  imageUrl?: string
  colorName?: string
}

// NameCell - Combined cell with checkbox, name, and variant count
interface NameCellProps extends CustomCellRendererProps<RowData> {
  isSelected: boolean
  onToggleSelect: (checked: boolean) => void
  editingCell: { itemId: string; field: string } | null
  onStartEdit: (itemId: string, field: string) => void
  onCancelEdit: () => void
  onSaveEdit: (itemId: string, field: string, value: string) => void
  navigateTab?: (
    rowId: string,
    colId: string,
    direction: 'forward' | 'backward'
  ) => void
  isReadOnly?: boolean
  onOpenCarousel?: (lineId: string) => void
}

export function NameCell(props: NameCellProps) {
  const storeEditing = useDagTableStore(s => s.editingCell)
  const clearEditing = useDagTableStore(s => s.clearEditing)

  const row = props.data
  if (!row) return null

  const {
    isSelected,
    onToggleSelect,
    editingCell,
    onStartEdit,
    onCancelEdit,
    onSaveEdit,
    navigateTab,
    isReadOnly,
    onOpenCarousel,
  } = props

  const description = String(row.cells?.['description'] ?? '')
  const sku = row.cells?.['plan_sku_num']
  const thumbnailUrl = row.images?.default_thumbnail_url ?? null
  const isGeneratingThumbnail = row.images?.thumbnail_status === 'PENDING'
  const isDescriptionEditable =
    !isReadOnly && (!sku || !!row.cells?.['is_new_sku'])
  const isEditing =
    isDescriptionEditable &&
    ((editingCell?.itemId === row.id && editingCell?.field === 'description') ||
      (storeEditing?.rowId === row.id && storeEditing?.colId === 'description'))

  const handleCancelEdit = () => {
    onCancelEdit()
    clearEditing()
  }

  return (
    <div className="flex items-center gap-2.5 w-full h-full px-4">
      {/* Checkbox */}
      <Checkbox
        checked={isSelected}
        onCheckedChange={checked => onToggleSelect(checked === true)}
        className="cursor-pointer"
      />

      {/* Thumbnail */}
      {isGeneratingThumbnail ? (
        <div className="w-[43px] h-[48px] bg-[#f8f8f8] rounded flex items-center justify-center shrink-0">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : thumbnailUrl ? (
        <button
          onClick={() => onOpenCarousel?.(row.id)}
          className="shrink-0 cursor-pointer"
        >
          <img
            src={thumbnailUrl}
            alt="Item thumbnail"
            loading="lazy"
            className="w-[43px] h-[48px] object-cover rounded"
          />
        </button>
      ) : (
        <div className="w-[43px] h-[48px] bg-[#f8f8f8] rounded flex items-center justify-center shrink-0">
          <ImageIcon className="size-6 text-muted-foreground" />
        </div>
      )}

      {/* Name and variant count */}
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <EditableCell
          value={description}
          onSave={value => onSaveEdit(row.id, 'description', value)}
          isEditing={isEditing}
          onStartEdit={
            isDescriptionEditable
              ? () => onStartEdit(row.id, 'description')
              : () => {}
          }
          onCancelEdit={handleCancelEdit}
          onTab={shiftKey =>
            navigateTab?.(
              row.id,
              'description',
              shiftKey ? 'backward' : 'forward'
            )
          }
        />
      </div>
    </div>
  )
}

// OptionRowCell - For expanded child items (options/variants)
interface OptionRowCellProps {
  option: OptionItem
}

export function OptionRowCell({ option }: OptionRowCellProps) {
  return (
    <div className="flex items-center gap-3 w-full h-full pl-[76px] pr-4">
      {/* Image thumbnail */}
      {option.imageUrl ? (
        <img
          src={option.imageUrl}
          alt={option.name}
          loading="lazy"
          className="w-[43px] h-[48px] object-cover rounded"
        />
      ) : (
        <div className="w-[43px] h-[48px] bg-gray-100 rounded flex items-center justify-center">
          <span className="text-xs text-muted-foreground">No img</span>
        </div>
      )}

      {/* Name and color */}
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <span className="font-semibold text-sm text-[#1c1c1c]">
          {option.name}
        </span>
        {option.colorName && (
          <span className="text-xs text-muted-foreground">
            Color: {option.colorName}
          </span>
        )}
      </div>
    </div>
  )
}

// SKU Cell - Wraps existing SkuSearchCell component
interface SkuCellProps extends CustomCellRendererProps<RowData> {
  editingCell: { itemId: string; field: string } | null
  onStartEdit: (itemId: string, field: string) => void
  onCancelEdit: () => void
  onSkuSave: (itemId: string, data: SkuSaveData, rowVersion: number) => void
  isReadOnly?: boolean
}

type SkuCellContext = {
  navigateTab?: (
    rowId: string,
    colId: string,
    direction: 'forward' | 'backward'
  ) => void
  getBulkSelectedRows?: (rowId: string) => DagRowRead[] | null
  onBulkCellEdit?: (
    selectedRows: DagRowRead[],
    columnId: string,
    value: unknown
  ) => void
}

export function SkuCell(props: SkuCellProps) {
  const storeEditing = useDagTableStore(s => s.editingCell)
  const clearEditing = useDagTableStore(s => s.clearEditing)

  const row = props.data
  if (!row) return null

  const { editingCell, onStartEdit, onCancelEdit, onSkuSave, isReadOnly } =
    props
  const skuNumber = String(row.cells?.['plan_sku_num'] ?? '')
  const isNewSku = Boolean(row.cells?.['is_new_sku'])
  const isEditing =
    (editingCell?.itemId === row.id && editingCell?.field === 'skuNumber') ||
    (storeEditing?.rowId === row.id && storeEditing?.colId === 'plan_sku_num')
  const context = props.context as SkuCellContext | undefined

  const handleCancelEdit = () => {
    onCancelEdit()
    clearEditing()
  }

  return (
    <div
      className="w-full h-full flex items-center gap-2 cursor-pointer"
      onDoubleClick={() =>
        !isReadOnly && !isEditing && onStartEdit(row.id, 'skuNumber')
      }
    >
      <SkuSearchCell
        value={skuNumber}
        isNewSku={isNewSku}
        onSave={data => {
          const selectedRows = context?.getBulkSelectedRows?.(row.id)
          if (selectedRows && context?.onBulkCellEdit) {
            context.onBulkCellEdit(selectedRows, 'plan_sku_num', data.skuNumber)
          } else {
            onSkuSave(row.id, data, row.row_version ?? 0)
          }
          clearEditing()
        }}
        isEditing={isEditing}
        onCancelEdit={handleCancelEdit}
        onTab={shiftKey => {
          context?.navigateTab?.(
            row.id,
            'plan_sku_num',
            shiftKey ? 'backward' : 'forward'
          )
        }}
      />
    </div>
  )
}

// Line Actions Cell - Action buttons (store, fork, more menu)
interface LineActionsCellProps extends CustomCellRendererProps<RowData> {
  onAssignStoreGroup: (itemId: string) => void
  onMultiloc: (item: PlanDetailItem) => void
  onViewSecondaryAttributes: (itemId: string) => void
  onPlanWaves: (itemId: string) => void
  onCopyLine: (itemId: string) => void
  onDeleteLine: (itemId: string) => void
  triggerAutoSave: () => void
  isReadOnly?: boolean
  onAssignParentSku?: (itemId: string) => void
  onManagePIAB?: (itemId: string) => void
  onCreatePricing?: (itemId: string) => void
  onCreateForecast?: (itemId: string) => void
  onManageImages?: (itemId: string) => void
  onOpenComments?: (lineRefId: string) => void
}

export function LineActionsCell(props: LineActionsCellProps) {
  const row = props.data
  if (!row || row.isOptionRow) return null

  const {
    onAssignStoreGroup,
    onMultiloc,
    onViewSecondaryAttributes,
    onPlanWaves,
    onCopyLine,
    onDeleteLine,
    triggerAutoSave,
    isReadOnly,
    onAssignParentSku,
    onManagePIAB,
    onCreatePricing,
    onCreateForecast,
    onManageImages,
    onOpenComments,
  } = props

  const item = rowDataToPlanDetailItem(row)
  const skuLevel = (row.cells?.['sku_level'] as string) ?? null
  const lineRefId = row.line_ref_id ?? null
  const commentCount = row.comment_count ?? 0

  return (
    <TooltipProvider>
      <div className="flex items-center justify-center gap-1">
        {/* Assign Store Model */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onAssignStoreGroup(item.id)}
              disabled={isReadOnly}
            >
              <Store className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Assign store model</p>
          </TooltipContent>
        </Tooltip>

        {/* Multiloc */}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onMultiloc(item)}
              disabled={isReadOnly}
            >
              <GitFork className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Multiloc</p>
          </TooltipContent>
        </Tooltip>

        {/* Comments */}
        {onOpenComments && lineRefId && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 relative"
                onClick={() => onOpenComments(lineRefId)}
                aria-label="Comments"
              >
                <MessageCircle className="h-4 w-4" />
                {commentCount > 0 && (
                  <Badge
                    variant="secondary"
                    className="absolute -right-1 -top-1 h-4 min-w-4 px-1 py-0 text-[10px] leading-none justify-center"
                  >
                    {commentCount}
                  </Badge>
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>
                {commentCount > 0
                  ? `${commentCount} comment${commentCount === 1 ? '' : 's'}`
                  : 'Comments'}
              </p>
            </TooltipContent>
          </Tooltip>
        )}

        {/* More Actions Menu */}
        <ItemActionsMenu
          itemId={item.id}
          onViewSecondaryAttributes={onViewSecondaryAttributes}
          onPlanWaves={onPlanWaves}
          onCopy={onCopyLine}
          onDelete={onDeleteLine}
          triggerAutoSave={triggerAutoSave}
          isReadOnly={isReadOnly}
          skuLevel={skuLevel}
          onAssignParentSku={onAssignParentSku}
          onManagePIAB={onManagePIAB}
          onCreatePricing={onCreatePricing}
          onCreateForecast={onCreateForecast}
          onManageImages={onManageImages}
        />
      </div>
    </TooltipProvider>
  )
}
